// src/services/nuestrosClientesService.js
// "Nuestros Clientes" -- álbumes de fotos/capturas (prueba social) +
// reseñas con estrellas moderadas. Toda lectura pública pasa por las RPCs
// (nunca SELECT directo) porque son las únicas que aplican el gate de
// "publicado" / token de vista previa -- ver la migración
// 20260910000000_nuestros_clientes.sql para el porqué.
import { supabase } from './supabase';

const BUCKET = 'clientes-fotos';
export const PREVIEW_TOKEN_KEY = 'nc_preview_token';

export const getStoredPreviewToken = () => {
  try { return localStorage.getItem(PREVIEW_TOKEN_KEY) || null; } catch { return null; }
};
export const storePreviewToken = (token) => {
  try { localStorage.setItem(PREVIEW_TOKEN_KEY, token); } catch {}
};

const publicUrl = (path) => supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;

// ── Lectura pública (anon o cliente logueado no-admin) ────────────────
export async function estaVisiblePublicamente() {
  const { data, error } = await supabase.rpc('nuestros_clientes_visible', {
    p_preview_token: getStoredPreviewToken(),
  });
  return !error && data === true;
}

export async function listarAlbumsPublicos() {
  const { data, error } = await supabase.rpc('listar_albums_publicos', {
    p_preview_token: getStoredPreviewToken(),
  });
  if (error || !data) return [];
  return data.map((a) => {
    const fotos = (a.fotos || []).map((f) => ({ id: f.id, url: publicUrl(f.storage_path), caption: f.caption, tipo: f.tipo || 'foto' }));
    // La portada nunca es un video (necesita una imagen fija de fondo) --
    // si eligieron una foto como portada se usa esa, si no, la primera
    // foto que haya (ignorando videos), y si el álbum es puro video, null.
    const portadaElegida = fotos.find((f) => f.id === a.portada_foto_id && f.tipo === 'foto');
    const portada = portadaElegida || fotos.find((f) => f.tipo === 'foto') || null;
    return {
      id: a.id,
      tituloPublico: a.titulo_publico,
      shareToken: a.share_token,
      fotos,
      portada,
    };
  });
}

export async function listarResenasPublicas() {
  const { data, error } = await supabase.rpc('listar_resenas_publicas', {
    p_preview_token: getStoredPreviewToken(),
  });
  if (error || !data) return [];
  return data.map((r) => ({ ...r, fotosUrls: (r.fotos || []).map(publicUrl) }));
}

export async function resumenResenasPublico() {
  const { data, error } = await supabase.rpc('resenas_resumen_publico', {
    p_preview_token: getStoredPreviewToken(),
  });
  if (error || !data || !data[0]) return { promedio: 0, total: 0 };
  return { promedio: Number(data[0].promedio) || 0, total: data[0].total || 0 };
}

// ── Reseña del cliente logueado ────────────────────────────────────────
export const CATEGORIAS_RESENA = [
  { id: 'vuelos', label: 'Vuelos' },
  { id: 'remesas', label: 'Remesas' },
  { id: 'documentos', label: 'Documentos' },
  { id: 'otro', label: 'Otro' },
];

// ── Dos caminos para dejar reseña ────────────────────────────────────────
// 1) Invitación de un solo uso: el admin la genera y se la manda
//    personalmente por WhatsApp a un viajero real -- la invitación misma
//    es la prueba de que es cliente, no hace falta pedir teléfono.
// 2) Camino abierto (el link que se pone en el grupo, para cualquiera):
//    solo hace falta estar logueado, pero el teléfono es obligatorio como
//    prueba, y el admin lo contrasta contra su lista de verificados (o
//    revisa a mano) antes de aprobar. Toda reseña nace "pendiente" en
//    ambos casos.
export const RESENA_INVITE_KEY = 'nc_resena_invite';

export const getStoredResenaInvite = () => {
  try { return localStorage.getItem(RESENA_INVITE_KEY) || null; } catch { return null; }
};
export const storeResenaInvite = (codigo) => {
  try { localStorage.setItem(RESENA_INVITE_KEY, codigo); } catch {}
};
export const clearResenaInvite = () => {
  try { localStorage.removeItem(RESENA_INVITE_KEY); } catch {}
};

export async function invitacionResenaValida(codigo) {
  if (!codigo) return false;
  const { data, error } = await supabase.rpc('invitacion_resena_valida', { p_codigo: codigo });
  return !error && data === true;
}

// Sube hasta 3 fotos propias del cliente que deja la reseña (sin editor
// de pixelado -- ver nota arriba) y devuelve sus storage_path para
// mandarle a la RPC. Cada quien solo puede subir dentro de su propia
// carpeta (resenas/<su-uid>/...), reforzado por policy de Storage.
async function subirFotosResena(files) {
  const { data: { user } } = await supabase.auth.getUser();
  const paths = [];
  for (const file of (files || []).slice(0, 3)) {
    const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
    const path = `resenas/${user.id}/${filename}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
      contentType: file.type || 'image/jpeg',
      cacheControl: '3600',
    });
    if (error) throw error;
    paths.push(path);
  }
  return paths;
}

export async function enviarResenaConInvitacion({ codigo, rating, comentario, fotos }) {
  const paths = await subirFotosResena(fotos);
  const { error } = await supabase.rpc('enviar_resena_con_invitacion', {
    p_codigo: codigo,
    p_rating: rating,
    p_comentario: comentario.trim(),
    p_fotos: paths,
  });
  if (error) throw error;
}

export async function enviarResena({ rating, comentario, telefono, fotos }) {
  const paths = await subirFotosResena(fotos);
  const { error } = await supabase.rpc('enviar_resena', {
    p_rating: rating,
    p_comentario: comentario.trim(),
    p_telefono: telefono.trim(),
    p_fotos: paths,
  });
  if (error) throw error;
}

// Reseña SIN login (página pública /nuestros-clientes): nombre y teléfono obligatorios, fotos opcionales
// (hasta 3, van a resenas/publico/). Igual que las demás, nace pendiente y solo se publica cuando el
// admin la aprueba.
async function subirFotosResenaPublica(files) {
  const paths = [];
  for (const file of (files || []).slice(0, 3)) {
    const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
    const path = `resenas/publico/${filename}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
      contentType: file.type || 'image/jpeg',
      cacheControl: '3600',
    });
    if (error) throw error;
    paths.push(path);
  }
  return paths;
}

export async function enviarResenaPublica({ nombre, rating, comentario, telefono, fotos }) {
  const paths = await subirFotosResenaPublica(fotos);
  const { error } = await supabase.rpc('enviar_resena_publica', {
    p_nombre: nombre.trim(),
    p_rating: rating,
    p_comentario: (comentario || '').trim(),
    p_telefono: telefono.trim(),
    p_fotos: paths,
  });
  if (error) throw error;
}

// Normaliza un teléfono a solo dígitos para poder comparar dos formatos
// distintos del mismo número (con espacios, +55, guiones, etc.).
export const normalizarTelefono = (str) => (str || '').replace(/\D/g, '');

export async function misResenas(userId) {
  const { data, error } = await supabase
    .from('clientes_resenas')
    .select('id, rating, comentario, categoria, estado, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data;
}

// ── Panel admin ────────────────────────────────────────────────────────
export async function adminListarAlbums() {
  const { data, error } = await supabase
    .from('clientes_albums')
    .select('id, titulo, titulo_publico, share_token, estado, publicado, orden, portada_foto_id, created_at, clientes_album_fotos!clientes_album_fotos_album_id_fkey(id, storage_path, orden, caption, tipo)')
    .order('orden', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data.map((a) => ({
    ...a,
    fotos: (a.clientes_album_fotos || [])
      .sort((x, y) => x.orden - y.orden)
      .map((f) => ({ id: f.id, path: f.storage_path, url: publicUrl(f.storage_path), orden: f.orden, caption: f.caption, tipo: f.tipo || 'foto' })),
  }));
}

export async function adminCrearAlbum({ titulo, tituloPublico }) {
  const { data, error } = await supabase
    .from('clientes_albums')
    .insert({ titulo: titulo.trim(), titulo_publico: tituloPublico?.trim() || null })
    .select('id, titulo, titulo_publico, share_token, estado, publicado, created_at')
    .single();
  if (error) throw error;
  return { ...data, fotos: [] };
}

export async function adminSetPortada(albumId, fotoId) {
  const { error } = await supabase.from('clientes_albums').update({ portada_foto_id: fotoId }).eq('id', albumId);
  if (error) throw error;
}

export async function adminActualizarAlbum(id, cambios) {
  const { error } = await supabase.from('clientes_albums').update(cambios).eq('id', id);
  if (error) throw error;
}

export async function adminEliminarAlbum(id) {
  const { error } = await supabase.from('clientes_albums').delete().eq('id', id);
  if (error) throw error;
}

// Reasigna el orden de TODOS los álbumes según su posición en la lista
// actual (0, 1, 2...) -- así cada movimiento ▲▼ se "autocorrige" sin
// importar en qué estado estuviera el orden antes.
export async function adminReordenarAlbums(pares) {
  const resultados = await Promise.all(
    pares.map(({ id, orden }) => supabase.from('clientes_albums').update({ orden }).eq('id', id))
  );
  const fallo = resultados.find((r) => r.error);
  if (fallo) throw fallo.error;
}

export async function adminSubirFoto(albumId, blob, orden = 0) {
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
  const path = `${albumId}/${filename}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    cacheControl: '3600',
  });
  if (upErr) throw upErr;
  const { data, error } = await supabase
    .from('clientes_album_fotos')
    .insert({ album_id: albumId, storage_path: path, orden })
    .select('id, storage_path')
    .single();
  if (error) throw error;
  return { id: data.id, path: data.storage_path, url: publicUrl(data.storage_path), orden, caption: null, tipo: 'foto' };
}

// Los videos se suben tal cual, sin pasar por el editor de pixelado --
// ver nota en el resumen del proyecto: pixelar video es mucho más
// complejo que una foto (habría que procesar cuadro por cuadro).
export async function adminSubirVideo(albumId, file, orden = 0) {
  const ext = file.name.split('.').pop() || 'mp4';
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const path = `${albumId}/${filename}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || 'video/mp4',
    cacheControl: '3600',
  });
  if (upErr) throw upErr;
  const { data, error } = await supabase
    .from('clientes_album_fotos')
    .insert({ album_id: albumId, storage_path: path, orden, tipo: 'video' })
    .select('id, storage_path')
    .single();
  if (error) throw error;
  return { id: data.id, path: data.storage_path, url: publicUrl(data.storage_path), orden, caption: null, tipo: 'video' };
}

export async function adminEliminarFoto(fotoId, path) {
  await supabase.storage.from(BUCKET).remove([path]);
  const { error } = await supabase.from('clientes_album_fotos').delete().eq('id', fotoId);
  if (error) throw error;
}

// Vuelve a editar una foto ya subida: sube la nueva versión a un path
// nuevo (evita que quede una URL vieja cacheada en el navegador de
// alguien) y borra el archivo anterior. La leyenda no cambia.
export async function adminReemplazarFoto(albumId, fotoId, oldPath, blob) {
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
  const path = `${albumId}/${filename}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    cacheControl: '3600',
  });
  if (upErr) throw upErr;
  const { data, error } = await supabase
    .from('clientes_album_fotos')
    .update({ storage_path: path })
    .eq('id', fotoId)
    .select('orden, caption')
    .single();
  if (error) throw error;
  await supabase.storage.from(BUCKET).remove([oldPath]);
  return { id: fotoId, path, url: publicUrl(path), orden: data.orden, caption: data.caption };
}

export async function adminActualizarCaptionFoto(fotoId, caption) {
  const { error } = await supabase.from('clientes_album_fotos').update({ caption: caption?.trim() || null }).eq('id', fotoId);
  if (error) throw error;
}

// Intercambia el orden de dos fotos consecutivas (mover ▲/▼ en el panel).
export async function adminIntercambiarOrdenFotos(fotoA, fotoB) {
  const { error: e1 } = await supabase.from('clientes_album_fotos').update({ orden: fotoB.orden }).eq('id', fotoA.id);
  if (e1) throw e1;
  const { error: e2 } = await supabase.from('clientes_album_fotos').update({ orden: fotoA.orden }).eq('id', fotoB.id);
  if (e2) throw e2;
}

// ── Lista de teléfonos verificados (agiliza la moderación) ──────────────
// No es una barrera dura -- un cliente real que no esté aquí igual puede
// enviar su reseña, solo le toca al admin revisarla a mano en WhatsApp.
export async function adminAgregarTelefonosVerificados(texto) {
  const filas = (texto || '')
    .split('\n')
    .map((linea) => {
      const telefono = normalizarTelefono(linea);
      const etiqueta = linea.replace(/[\d\s+()-]/g, '').trim();
      return telefono.length >= 8 ? { telefono, etiqueta: etiqueta || null } : null;
    })
    .filter(Boolean);
  if (filas.length === 0) return 0;
  const { error } = await supabase.from('clientes_telefonos_verificados').upsert(filas, { onConflict: 'telefono' });
  if (error) throw error;
  return filas.length;
}

export async function adminListarTelefonosVerificados() {
  const { data, error } = await supabase
    .from('clientes_telefonos_verificados')
    .select('telefono, etiqueta, created_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function adminEliminarTelefonoVerificado(telefono) {
  const { error } = await supabase.from('clientes_telefonos_verificados').delete().eq('telefono', telefono);
  if (error) throw error;
}

// ── Invitaciones a dejar reseña (link personal, un solo uso) ────────────
export async function adminCrearInvitacionResena(etiqueta) {
  const { data, error } = await supabase
    .from('clientes_resena_invitaciones')
    .insert({ etiqueta: etiqueta?.trim() || null })
    .select('id, codigo, etiqueta, usado, created_at')
    .single();
  if (error) throw error;
  return data;
}

export async function adminListarInvitacionesResena() {
  const { data, error } = await supabase
    .from('clientes_resena_invitaciones')
    .select('id, codigo, etiqueta, usado, created_at, used_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function adminEliminarInvitacionResena(id) {
  const { error } = await supabase.from('clientes_resena_invitaciones').delete().eq('id', id);
  if (error) throw error;
}

export async function adminListarResenas(estado) {
  let query = supabase
    .from('clientes_resenas')
    .select('id, rating, comentario, categoria, telefono, estado, respuesta_publica, created_at, user_id, nombre, fotos, profiles(name)')
    .order('created_at', { ascending: false });
  if (estado) query = query.eq('estado', estado);
  const { data, error } = await query;
  if (error) throw error;
  return data.map((r) => ({ ...r, nombre: r.profiles?.name || r.nombre || '(sin nombre)', fotosUrls: (r.fotos || []).map(publicUrl) }));
}

export async function adminModerarResena(id, estado, respuestaPublica) {
  const { error } = await supabase
    .from('clientes_resenas')
    .update({ estado, respuesta_publica: respuestaPublica?.trim() || null, moderated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function adminEliminarResena(id) {
  const { error } = await supabase.from('clientes_resenas').delete().eq('id', id);
  if (error) throw error;
}

export async function adminGetConfig() {
  const { data, error } = await supabase
    .from('clientes_config')
    .select('publicado, preview_token, google_review_link')
    .eq('id', 1)
    .single();
  if (error) throw error;
  return data;
}

export async function adminActualizarConfig(cambios) {
  const { error } = await supabase
    .from('clientes_config')
    .update({ ...cambios, updated_at: new Date().toISOString() })
    .eq('id', 1);
  if (error) throw error;
}

export async function adminGetGoogleReviewLink() {
  const { data } = await supabase.from('clientes_config').select('google_review_link').eq('id', 1).single();
  return data?.google_review_link || null;
}

// Para cualquier cliente logueado (no solo el admin) -- clientes_config
// tiene RLS que solo deja leer al admin, así que esto pasa por una RPC.
export async function getGoogleReviewLinkPublico() {
  const { data, error } = await supabase.rpc('google_review_link_publico');
  return error ? null : data;
}

// ── Analytics: visitas a la página de Nuestros Clientes (sin datos
// personales) -- session_id aleatorio en localStorage, solo para aproximar
// visitantes únicos, mismo criterio que vuelosGuyanaService.
const NC_SESSION_KEY = 'nc_visita_session';
const getClientesSessionId = () => {
  let id = localStorage.getItem(NC_SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(NC_SESSION_KEY, id);
  }
  return id;
};

// Exclusión de las propias visitas del admin (probar el link no debe
// inflar las estadísticas) -- toggle por navegador, igual que en Vuelos.
const NC_NO_TRACK_KEY = 'nc_visita_no_track';
export const isClientesTrackingOptedOut = () => localStorage.getItem(NC_NO_TRACK_KEY) === '1';
export const setClientesTrackingOptOut = (activo) => {
  if (activo) localStorage.setItem(NC_NO_TRACK_KEY, '1');
  else localStorage.removeItem(NC_NO_TRACK_KEY);
};

// Fire-and-forget: nunca debe interrumpir la carga de la página si falla.
export function logVisitaClientes(origen) {
  if (isClientesTrackingOptedOut()) return;
  (async () => {
    try {
      await supabase.rpc('registrar_evento_clientes', { p_origen: origen, p_session_id: getClientesSessionId() });
    } catch {}
  })();
}

// Admin: eventos crudos de los últimos N días -- la agregación por
// día/semana/mes se hace en el componente. Paginado por si algún día se
// pasa de las 1000 filas por consulta (tope de PostgREST), mismo criterio
// que getVuelosEventos.
const NC_EVENTOS_PAGE_SIZE = 1000;
export async function adminGetClientesEventos(desdeDias = 90) {
  const todas = [];
  let desdeIndice = 0;
  while (true) {
    let query = supabase
      .from('clientes_eventos')
      .select('*')
      .order('created_at', { ascending: false })
      .range(desdeIndice, desdeIndice + NC_EVENTOS_PAGE_SIZE - 1);
    if (desdeDias != null) {
      const desde = new Date();
      desde.setDate(desde.getDate() - desdeDias);
      query = query.gte('created_at', desde.toISOString());
    }
    const { data, error } = await query;
    if (error || !data) break;
    todas.push(...data);
    if (data.length < NC_EVENTOS_PAGE_SIZE) break;
    desdeIndice += NC_EVENTOS_PAGE_SIZE;
  }
  return todas;
}

// ── Pixelado manual (canvas) ────────────────────────────────────────────
// Aplica un efecto de mosaico (no rectángulo negro sólido) sobre cada zona
// marcada: promedia el color de cada bloque de blockSize x blockSize y lo
// rellena con ese color -- el mismo look que usan los editores de fotos.
export function pixelarZonas(ctx, zonas, blockSize = 14) {
  zonas.forEach(({ x, y, w, h }) => {
    for (let by = y; by < y + h; by += blockSize) {
      for (let bx = x; bx < x + w; bx += blockSize) {
        const bw = Math.min(blockSize, x + w - bx);
        const bh = Math.min(blockSize, y + h - by);
        if (bw <= 0 || bh <= 0) continue;
        const data = ctx.getImageData(bx, by, bw, bh).data;
        let r = 0, g = 0, b = 0, count = data.length / 4;
        for (let i = 0; i < data.length; i += 4) { r += data[i]; g += data[i + 1]; b += data[i + 2]; }
        ctx.fillStyle = `rgb(${Math.round(r / count)},${Math.round(g / count)},${Math.round(b / count)})`;
        ctx.fillRect(bx, by, bw, bh);
      }
    }
  });
}
